import { $channel } from "@/controllers/channel";
import { $user } from "@/controllers/user";
import { $event } from "@/core/event";
import { PluginManager } from "@/core/manager";
import { $event_subscribe } from "@/core/subscribe";

// import type { PageBehaviorParams } from "@/interface/api";
import type { PageEvent } from "@/core/subscribe";
import type {
  SdkOptions,
  SdkContext,
  SdkImportsRequires,
} from "@/interface/sdk";
// import type { TrackerPlugin } from "@/plugins/tracker";

/**
 * SDK 主类
 *
 * 提供插件化架构的核心功能，负责协调插件管理器、事件系统和用户控制器。
 *
 * @example
 * ```typescript
 * const sdk = new Sdk();
 * await sdk.init({
 *   appId: 'my-app',
 *   imports: [TrackerPlugin, MessagePlugin]
 * });
 * ```
 */
export class Sdk {
  /** 插件管理器实例 */
  plugins!: PluginManager;
  private pluginList: SdkImportsRequires = {
    imports: [],
    requires: [],
  };

  // track!: (s: "form" | "tel" | "human") => Promise<boolean>;
  /**
   * 创建 SDK 实例
   *
   * 构造函数会自动初始化用户控制器。
   */
  constructor() {
    void $user.init();
  }

  /**
   * 初始化 SDK 并加载插件
   *
   * 此方法会：
   * 1. 创建插件管理器
   * 2. 按依赖顺序加载插件
   * 3. 等待用户准备完成后调用插件的 userEnd 生命周期
   *
   * @param o - SDK 配置选项
   * @param o.appId - 应用 ID
   * @param o.floating - 可选，浮动组件配置
   * @param o.track - 可选，埋点配置
   * @param o.imports - 可选，要加载的插件列表
   * @param o.requires - 可选，插件依赖配置
   *
   * @returns Promise，包含 SDK 实例、事件 API 和事件订阅系统
   *
   * @example
   * ```typescript
   * const { sdk, $event, $event_subscribe } = await sdk.init({
   *   appId: 'my-app',
   *   imports: [TrackerPlugin, MessagePlugin],
   *   track: { autoPage: true }
   * });
   * ```
   */
  async init(o: SdkOptions): Promise<{
    sdk: Sdk;
    $event: typeof $event;
    $event_subscribe: typeof $event_subscribe;
  }> {
    const context: SdkContext = {
      sdk: this,
      options: o,
    };

    if (o.appId != "") {
      await $channel.init(o.appId);
    }
    this.plugins = new PluginManager(context);

    if (o.imports != undefined && o.imports.length) {
      await this.plugins.load(o.imports, o.requires ?? []);
    } else {
      await this.plugins.load(
        this.pluginList.imports,
        this.pluginList.requires ?? [],
      );
    }

    $user.$end.subscribe(async () => {
      await this.plugins.callAfterUserReady();
    });

    // const track = this.plugins.getPluginByName("Tracker") as TrackerPlugin;
    // console.log(85666, track);
    // if (track != undefined) {
    //   this.track = (s): Promise<boolean> => {
    //     const json: { [key: string]: PageBehaviorParams["behavior"] } = {
    //       form: "clue_form",
    //       tel: "clue_tel",
    //       human: "clue_human",
    //     };
    //     return track.trackCustom(json[s]);
    //   };
    // }
    return { sdk: this, $event, $event_subscribe: $event_subscribe };
  }

  track(s: PageEvent["track"]): void {
    $event_subscribe.emit("track", s);
  }

  setPlugins(o: SdkImportsRequires): void {
    this.pluginList.imports = this.pluginList.imports.concat(o.imports);
    this.pluginList.requires = this.pluginList.requires.concat(o.requires);
  }
}
